// Formatting and parsing helpers shared by the match and pick tables.

const IMAGE_BUCKET = 'https://betcopaverde-bucket.s3.amazonaws.com';
const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august',
  'september', 'october', 'november', 'december'];

export function imageUrl(path) {
  return `${IMAGE_BUCKET}/${path || 'Default'}.jpg`;
}

export function capitalizeName(name) {
  if (!name) {
    return '';
  }
  return String(name).replace(/-/g, ' ').replace(/\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

// "37.760000000000005%" or 0.3776 -> 0.3776; null when missing.
export function parseProb(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return value > 1 ? value / 100 : value;
  }
  const number = parseFloat(String(value).replace('%', ''));
  if (Number.isNaN(number)) {
    return null;
  }
  return String(value).includes('%') || number > 1 ? number / 100 : number;
}

export function parseOdds(value) {
  const number = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(number) && number > 1 ? number : null;
}

export function formatPct(prob, digits = 1) {
  return prob === null || prob === undefined ? '–' : `${(prob * 100).toFixed(digits)}%`;
}

export function formatOdds(odds) {
  return odds ? odds.toFixed(2) : '–';
}

export function formatMoney(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : '–';
}

// Expected return of a 1-unit bet at these odds if our probability is right (0.05 = +5%).
export function edge(prob, odds) {
  return prob === null || odds === null ? null : prob * odds - 1;
}

export function formatEdge(value) {
  if (value === null || value === undefined) {
    return '–';
  }
  const pct = (value * 100).toFixed(1);
  return value > 0 ? `+${pct}%` : `${pct}%`;
}

// Kick-off strings come already shifted to the viewer's time zone by the server, as either
// "2026-10-17 15:00:00" (server-copa) or "03:00 PM October 17" (production API). Both are read as
// local wall-clock times; new Date("2026-10-17 15:00:00") is not portable (Safari rejects it).
export function parseKickoff(value, today = new Date()) {
  if (!value) {
    return null;
  }
  let m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (m) {
    return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  }
  m = String(value).match(/^(\d{1,2}):(\d{2}) ?([AP]M) ([A-Za-z]+) (\d{1,2})/i);
  if (m) {
    const month = MONTHS.indexOf(m[4].toLowerCase());
    if (month < 0) {
      return null;
    }
    let hour = (+m[1]) % 12;
    if (m[3].toUpperCase() === 'PM') {
      hour += 12;
    }
    let year = today.getFullYear();
    // A December fixture viewed in January (or the reverse) belongs to the neighbouring year.
    if (month - today.getMonth() > 6) {
      year -= 1;
    } else if (today.getMonth() - month > 6) {
      year += 1;
    }
    return new Date(year, month, +m[5], hour, +m[2]);
  }
  return null;
}

export function formatKickoffTime(date) {
  return date ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
}

export function formatKickoffDay(date, today = new Date()) {
  if (!date) {
    return '';
  }
  const diff = Math.round((startOfDay(date) - startOfDay(today)) / 86400000);
  if (diff === 0) {
    return 'Today';
  }
  if (diff === 1) {
    return 'Tomorrow';
  }
  if (diff === -1) {
    return 'Yesterday';
  }
  return date.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
}

export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// Local calendar date as YYYY-MM-DD (toISOString would give the UTC date).
export function isoDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function parseIsoDate(value) {
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
}

export function addDays(date, days) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}
